import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './AdminScalpingLab.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('sessionToken')}` });

const num = (n, d = 2) =>
  n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: d });
const pct = (n) => (n == null ? '—' : `${(Number(n) * 100).toFixed(0)}%`);
const usd = (n) => (n == null ? '—' : `${Number(n) < 0 ? '-' : ''}$${Math.abs(Number(n)).toFixed(2)}`);
const dur = (ms) => (ms == null ? '—' : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`);

const WINDOWS = [
  ['6', '6h'],
  ['24', '24h'],
  ['72', '3d'],
  ['168', '7d'],
  ['0', 'All'],
];

const SYNTH_SCENARIOS = [
  ['ideal', 'Ideal reverts'],
  ['target', 'Target thesis'],
  ['realistic', 'Realistic mix'],
  ['hostile', 'Hostile tails'],
  ['no_fill', 'No fills'],
];

const SYNTH_DURATIONS = [
  ['24', '1 day'],
  ['168', '7 days'],
];

/** Normalize async poll / API payloads so the results panel always has a `run` object. */
function normalizeSimPayload(raw) {
  if (!raw) return null;
  const perSymbol = (raw.perSymbol || []).map((s) => ({
    symbol: s.symbol,
    num_trades: s.num_trades ?? s.numTrades,
    num_wins: s.num_wins ?? s.numWins,
    leverage: s.leverage,
    net_usd: s.net_usd ?? s.netUsd,
    omni_revenue_usd: s.omni_revenue_usd ?? s.omniRevenueUsd,
    avg_net_bps_per_trade: s.avg_net_bps_per_trade ?? s.avgNetBpsPerTrade,
    avg_hold_ms: s.avg_hold_ms ?? s.avgHoldMs,
    exitReasons: s.exitReasons,
  }));
  let run = raw.run;
  if (!run && raw.summary?.num_trades != null) {
    const s = raw.summary;
    run = {
      kind: 'backtest',
      numTrades: s.num_trades,
      numWins: s.num_wins,
      winRate: s.win_rate,
      avgNetBpsPerTrade: s.avg_net_bps_per_trade,
      netUsd: s.net_usd,
      omniRevenueUsd: s.omni_revenue_usd,
      avgHoldMs: s.avg_hold_ms,
      maxDrawdownUsd: s.max_drawdown_usd,
      marginUsd: s.margin_usd ?? s.notional_usd,
      netUsdAt1x: s.netUsdAt1x ?? s.net_usd_at_1x,
      marginRoiPct: s.marginRoiPct ?? s.margin_roi_pct,
      avgLeverage: s.avgLeverage ?? s.avg_leverage,
    };
  }
  const hasPanel =
    run ||
    perSymbol.length > 0 ||
    (raw.exitReasons && Object.values(raw.exitReasons).some((v) => v > 0));
  if (!hasPanel) return raw;
  return { ...raw, run: run || raw.run, perSymbol };
}

export default function AdminScalpingLab({ deferLoad = false }) {
  const [hours, setHours] = useState('24');
  const [analytics, setAnalytics] = useState(null);
  const [runs, setRuns] = useState([]);
  const [liveRun, setLiveRun] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [loading, setLoading] = useState(!deferLoad);
  const [labActive, setLabActive] = useState(!deferLoad);
  const [running, setRunning] = useState(false);
  const [runningSynth, setRunningSynth] = useState(false);
  const [runningMatrix, setRunningMatrix] = useState(false);
  const [synthScenario, setSynthScenario] = useState('realistic');
  const [synthHours, setSynthHours] = useState('168');
  const [err, setErr] = useState(null);
  const [backtestStatus, setBacktestStatus] = useState(null);
  const resultRef = useRef(null);

  const loadAnalytics = useCallback(async () => {
    try {
      const q = hours === '0' ? '' : `?hours=${hours}`;
      const { data } = await axios.get(`${API_BASE}/scalping/analytics${q}`, { headers: authHeaders() });
      setAnalytics(data);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  }, [hours]);

  const loadRuns = useCallback(async () => {
    try {
      const [simRes, paperRes] = await Promise.all([
        axios.get(`${API_BASE}/scalping/simulations?limit=15`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/scalping/paper/summary`, { headers: authHeaders() }).catch(() => ({ data: null })),
      ]);
      setRuns(simRes.data.runs || []);
      setLiveRun(paperRes.data?.run || null);
    } catch (e) {
      /* non-fatal */
    }
  }, []);

  useEffect(() => {
    if (!labActive) return undefined;
    setLoading(true);
    Promise.all([loadAnalytics(), loadRuns()]).finally(() => setLoading(false));
    return undefined;
  }, [labActive, loadAnalytics, loadRuns]);

  const pollBacktestJob = async (jobId, { isAll = false, startedAtMs = Date.now() } = {}) => {
    const maxMs = isAll ? 60 * 60 * 1000 : 30 * 60 * 1000;
    const started = Date.now();
    setBacktestStatus('Backtest running in background…');
    while (Date.now() - started < maxMs) {
      let data;
      try {
        const res = await axios.get(`${API_BASE}/scalping/simulate/jobs/${jobId}`, {
          headers: authHeaders(),
          timeout: 30000,
          validateStatus: (s) => s === 200 || s === 500,
        });
        data = res.data;
        if (res.status === 500 && data?.status === 'failed') {
          throw new Error(data.error || 'Backtest failed');
        }
      } catch (e) {
        if (e.response?.status === 404) {
          throw new Error(
            `Job ${jobId} not found on server — async jobs may be broken (pm2 cluster?). Redeploy backtestJobs.js and restart omni-api.`
          );
        }
        throw e;
      }
      if (data.status === 'running') {
        const elapsed = data.elapsedSec ?? Math.round((Date.now() - started) / 1000);
        const minHint = isAll ? 'All-data: 5–45 min typical' : 'usually 1–10 min';
        setBacktestStatus(`Backtest running… ${elapsed}s (${minHint})`);
        await new Promise((r) => setTimeout(r, 2000));
        continue;
      }
      if (data.status === 'done') {
        setBacktestStatus(null);
        return data;
      }
      throw new Error(data?.error || `Unexpected job poll status: ${data?.status ?? 'none'}`);
    }

    // Poll timed out — backtest may still have finished and saved a run row.
    try {
      const { data: hist } = await axios.get(`${API_BASE}/scalping/simulations?limit=8`, {
        headers: authHeaders(),
      });
      const cutoff = startedAtMs - 5000;
      const recent = (hist.runs || []).find((r) => {
        if (r.kind !== 'backtest') return false;
        const t = r.createdAt ? Date.parse(`${r.createdAt}Z`) : 0;
        return t >= cutoff;
      });
      if (recent) {
        setBacktestStatus(null);
        return normalizeSimPayload({
          ok: true,
          recorded: true,
          run: recent,
          perSymbol: [],
          exitReasons: {},
        });
      }
    } catch {
      /* fall through */
    }

    throw new Error(
      isAll
        ? 'Backtest poll timed out after 60 minutes. Check Run history for a new backtest row — if present, results saved. Otherwise use 7d window or retry after deploy.'
        : 'Backtest timed out after 30 minutes'
    );
  };

  const runBacktest = async () => {
    setRunning(true);
    setErr(null);
    setBacktestStatus(null);
    const isAll = hours === '0';
    const windowLabel = isAll ? 'all data' : `${hours}h`;
    try {
      const body = isAll ? { allData: true } : { hours: Number(hours) };
      const hNum = Number(hours);
      const btTimeoutMs = isAll ? 3600000 : hNum >= 168 ? 1800000 : hNum >= 72 ? 1560000 : 600000;
      setBacktestStatus(`Running recorded backtest (${windowLabel})… may take ${hNum >= 72 ? '5–20' : '0.5–3'} min`);
      const { data } = await axios.post(`${API_BASE}/scalping/simulate`, body, {
        headers: authHeaders(),
        timeout: btTimeoutMs,
      });
      let normalized = normalizeSimPayload(data);
      if (!normalized?.run) {
        try {
          const { data: hist } = await axios.get(`${API_BASE}/scalping/simulations?limit=5`, {
            headers: authHeaders(),
          });
          const latest = (hist.runs || []).find((r) => r.kind === 'backtest');
          if (latest) {
            normalized = normalizeSimPayload({ ...normalized, run: latest, recorded: true });
          }
        } catch {
          /* optional fallback */
        }
      }
      if (
        !normalized?.run &&
        !(normalized?.perSymbol?.length) &&
        !Object.values(normalized?.exitReasons || {}).some((v) => v > 0)
      ) {
        setErr(
          'Backtest finished but returned no results. Deploy backend/routes/scalping.js + backtestJobs.js, restart omni-api (pm2 instances=1), then retry.'
        );
        setLastRun(null);
      } else {
        setLastRun(normalized);
        setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
      await loadRuns();
    } catch (e) {
      const isTimeout =
        e.code === 'ECONNABORTED' || String(e.message || '').toLowerCase().includes('timeout');
      if (isTimeout) {
        setErr(
          hours === '0' || Number(hours) >= 168
            ? 'Backtest exceeded 10 min — server may still be on old code. Deploy paperSim.js + routes/scalping.js, restart omni-api. Use 6h/24h meanwhile (works in ~30s).'
            : e.response?.data?.error || e.message
        );
      } else {
        setErr(e.response?.data?.error || e.message);
      }
    } finally {
      setRunning(false);
      setBacktestStatus(null);
    }
  };

  const runSyntheticMatrix = async () => {
    setRunningMatrix(true);
    setErr(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/scalping/simulate/synthetic/matrix`,
        {
          scenario: synthScenario,
          durationHours: Number(synthHours),
          seed: Date.now() % 100000,
        },
        { headers: authHeaders(), timeout: 300000 }
      );
      setLastRun(data);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setRunningMatrix(false);
    }
  };

  const runSynthetic = async () => {
    setRunningSynth(true);
    setErr(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/scalping/simulate/synthetic`,
        {
          scenario: synthScenario,
          durationHours: Number(synthHours),
          eventsPerHour: 4.5,
          seed: Date.now() % 100000,
        },
        { headers: authHeaders(), timeout: 180000 }
      );
      setLastRun(data);
      await loadRuns();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setRunningSynth(false);
    }
  };

  const a = analytics || {};
  const be = a.breakeven || {};
  const profiles = a.profiles || {};
  const sizing = a.sizing || {};
  const rows = a.symbols || [];

  return (
    <div className="lab">
      {deferLoad && !labActive && (
        <div className="lab-defer">
          <p>Measurement &amp; backtest lab loads on demand (keeps admin fast).</p>
          <button type="button" className="lab-run" onClick={() => setLabActive(true)}>
            Load measurement &amp; backtest lab
          </button>
        </div>
      )}
      {(!deferLoad || labActive) && (
      <>
      <div className="lab-head">
        <div>
          <h3>Measurement &amp; Backtest</h3>
          <p className="lab-sub">Net-to-trader backtest with margin × leverage position sizing, exchange fees, commission &amp; profit share.</p>
        </div>
        <div className="lab-window">
          {WINDOWS.map(([v, label]) => (
            <button key={v} className={hours === v ? 'on' : ''} onClick={() => setHours(v)}>{label}</button>
          ))}
        </div>
      </div>

      {err && <div className="lab-err">{err}</div>}

      <div className="lab-workflow">
        <strong>Synthetic dev loop — three scenarios, three jobs</strong>
        <span>
          <em>Ideal</em> must be green (engine proof). <em>Target</em> = research thesis bar (~62% TP).{' '}
          <em>Realistic</em> — optional; headline edge unreliable until generator rewrite. Use <em>recorded 72h</em> as regression gate.{' '}
          Recorded All runs async (no 5 min timeout). Matrix tests all symbols at once.
        </span>
      </div>

      {!a.hip3TradingEnabled && (
        <div className="lab-hip3-off">
          <strong>HIP-3 trading sidelined</strong> — backtest and live paper run crypto only (BTC/SOL when ETH off).
          Phase 2 still measures HIP-3 data.
        </div>
      )}
      {profiles.crypto?.symbolOverrides?.ETH?.trading_disabled && (
        <div className="lab-hip3-off">
          <strong>ETH sidelined</strong> — stop-heavy on this feed; data only.
        </div>
      )}
      {profiles.crypto?.symbolOverrides?.BTC?.trading_disabled && (
        <div className="lab-sol-only">
          <strong>SOL-only trading</strong> — v19: bleed 45s · maker stop · stale 100s/−8 bps.
        </div>
      )}
      {profiles.crypto?.symbolOverrides?.BTC?.live_paper_disabled && !profiles.crypto?.symbolOverrides?.BTC?.trading_disabled && (
        <div className="lab-sol-only">
          <strong>v34 — PnL bleed 10s/−5 bps (no dev-widen)</strong> · SOL live paper · matrix BTC+ETH+SOL (~1 min).
        </div>
      )}

      <div className="lab-breakeven">
        <span>Package 2 profiles — breakeven (maker→maker, incl. commission):</span>
        <strong>crypto {num(be.crypto, 2)} bps</strong>
        <strong>HIP-3 {num(be.hip3, 2)} bps</strong>
        {a.hip3SessionNow && <strong>HIP-3 session (stocks): {a.hip3SessionNow}</strong>}
        {sizing.leverageCapped && (
          <strong className="lab-warn">Max leverage ({sizing.maxLeverage}x) is capping crypto — raise in admin settings</strong>
        )}
        <span className="lab-note">
          <strong>SOL + BTC + ETH backtest</strong> · profile v34 · synthetic v25
          {' · '}Effective lev: crypto {sizing.leverageCrypto ?? 5}x · HIP-3 {sizing.leverageHip3 ?? 3}x
          {' · '}${sizing.marginUsd ?? 2000} margin/trade
        </span>
      </div>

      <h4>Phase 2 — dislocation &amp; reversion measurement</h4>
      <div className="lab-tablewrap">
        <table className="lab-table">
          <thead>
            <tr>
              <th>Symbol</th><th>Class</th><th>Samples</th><th>Events</th><th>Events/hr</th>
              <th>Reversion</th><th>Med. revert</th><th>Med. captured</th>
              <th>Breakeven</th><th>Net edge</th><th>Verdict</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.symbol} className={!r.enoughData ? 'lab-dim' : ''}>
                <td><strong>{r.symbol}</strong> <span className="lab-cat">{r.category || r.dex}</span></td>
                <td><span className={`lab-kind ${r.profileClass || ''}`}>{r.profileClass || '—'}</span></td>
                <td>{num(r.sampleCount, 0)}</td>
                <td>{r.enoughData ? num(r.dislocationEvents, 0) : '—'}</td>
                <td>{r.enoughData ? num(r.eventsPerHour, 1) : '—'}</td>
                <td>{r.enoughData ? pct(r.reversionRate) : '—'}</td>
                <td>{r.enoughData ? dur(r.medianTimeToRevertMs) : '—'}</td>
                <td>{r.enoughData ? `${num(r.medianCapturedBps, 2)} bps` : '—'}</td>
                <td>{num(r.breakevenBps, 2)} bps</td>
                <td className={r.netEdgeEstimateBps > 0 ? 'pos' : r.netEdgeEstimateBps < 0 ? 'neg' : ''}>
                  {r.enoughData ? `${num(r.netEdgeEstimateBps, 2)} bps` : '—'}
                </td>
                <td>{!r.enoughData ? <span className="lab-wait">need data</span> : r.edgePositive ? <span className="lab-yes">edge ✓</span> : <span className="lab-no">no edge</span>}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={11} className="lab-empty">{loading ? 'Loading…' : 'No recorded data in window yet.'}</td></tr>}
          </tbody>
        </table>
      </div>

      <h4>Phase 3 — paper backtest (realistic maker fills + costs)</h4>
      <div className="lab-synth">
        <strong>Synthetic tape (instant)</strong>
        <span className="lab-note">
          Generate fake dislocation/reversion paths — hundreds of trades per symbol in seconds.
          Matrix runs all enabled symbols at once. Use matrix for 8–9 asset tuning; recorded 6h/24h for real-tape spot checks.
        </span>
        <div className="lab-synth-controls">
          <div className="lab-window">
            {SYNTH_SCENARIOS.map(([v, label]) => (
              <button key={v} className={synthScenario === v ? 'on' : ''} onClick={() => setSynthScenario(v)}>{label}</button>
            ))}
          </div>
          <div className="lab-window">
            {SYNTH_DURATIONS.map(([v, label]) => (
              <button key={v} className={synthHours === v ? 'on' : ''} onClick={() => setSynthHours(v)}>{label}</button>
            ))}
          </div>
          <button className="lab-run lab-run-synth" disabled={runningSynth || runningMatrix || running} onClick={runSynthetic}>
            {runningSynth ? 'Generating…' : `Run synthetic (${SYNTH_DURATIONS.find(([v]) => v === synthHours)?.[1] || synthHours + 'h'})`}
          </button>
          <button className="lab-run lab-run-matrix" disabled={runningMatrix || runningSynth || running} onClick={runSyntheticMatrix}>
            {runningMatrix ? 'Matrix…' : 'Run all assets (synthetic matrix)'}
          </button>
        </div>
      </div>
      <div className="lab-actions">
        {hours === '0' && (
          <div className="lab-recorded-hint">
            <strong>Recorded All</strong> — select <em>All</em> in the time window above (top-right), then run below. Runs async; may take 2–10 min.
          </div>
        )}
        <button className="lab-run" disabled={running || runningSynth || runningMatrix} onClick={runBacktest}>
          {running
            ? backtestStatus || 'Backtest running…'
            : `Run recorded backtest (${hours === '0' ? 'all data' : hours + 'h'})`}
        </button>
        <span className="lab-note">
          {hours === '0' || Number(hours) >= 168
            ? 'Long windows auto-decimate to ~150k snapshots/symbol so results return in minutes.'
            : '6h/24h = fastest recorded check. Results appear below when done.'}
        </span>
      </div>
      {backtestStatus && <div className="lab-backtest-status">{backtestStatus}</div>}
      {err && <div className="lab-err lab-err-near">{err}</div>}

      {lastRun?.run && (lastRun.recorded || !lastRun.syntheticMeta) && !lastRun.syntheticMeta && (
        <div className="lab-recorded-meta">
          <strong>Recorded backtest result</strong>
          {lastRun.run.label ? ` — ${lastRun.run.label}` : ''}
          {lastRun.run.windowStart && lastRun.run.windowEnd && (
            <span>
              {' '}
              · window {new Date(lastRun.run.windowStart).toLocaleDateString()} –{' '}
              {new Date(lastRun.run.windowEnd).toLocaleDateString()}
            </span>
          )}
        </div>
      )}

      {lastRun?.syntheticMeta && (
        <div className="lab-synth-meta">
          Synthetic: {lastRun.syntheticMeta.label} · {num(lastRun.syntheticMeta.eventCount, 0)} dislocation events ·{' '}
          {num(lastRun.syntheticMeta.snapshotCount, 0)} snapshots · seed {lastRun.syntheticMeta.seed}
        </div>
      )}

      {lastRun?.run && (
        <div className="lab-result" ref={resultRef}>
          <div className="lab-edge-banner">
            <strong>Strategy health = Avg net bps</strong> ({num(lastRun.run.avgNetBpsPerTrade, 2)} bps) — unchanged by leverage.
            Win rate {pct(lastRun.run.winRate)} can mislead — check avg bps per exit bucket below.
            Phase 2 measures oracle capture; Phase 3 prices touch round-trips. Judge by <strong>avg net bps</strong>, not win %.
          </div>
          <div className="lab-cards">
            <div className={`lab-c lab-c-primary ${(lastRun.run.avgNetBpsPerTrade ?? 0) >= 0 ? 'good' : 'bad'}`}>
              <span>Edge (avg bps)</span><strong>{num(lastRun.run.avgNetBpsPerTrade, 2)} bps</strong>
            </div>
            <div className="lab-c"><span>Trades</span><strong>{num(lastRun.run.numTrades, 0)}</strong></div>
            <div className="lab-c"><span>Win rate</span><strong>{pct(lastRun.run.winRate)}</strong></div>
            <div className={`lab-c ${lastRun.run.netUsd >= 0 ? 'good' : 'bad'}`}><span>Net (trader, leveraged)</span><strong>{usd(lastRun.run.netUsd)}</strong></div>
            <div className="lab-c lab-c-sub"><span>↳ at 1× equiv</span><strong>{usd(lastRun.summary?.netUsdAt1x ?? lastRun.run.netUsdAt1x)}</strong></div>
            <div className="lab-c lab-c-sub"><span>↳ ROI on margin</span><strong>{lastRun.summary?.marginRoiPct != null ? `${num(lastRun.summary.marginRoiPct, 2)}%` : lastRun.run.marginRoiPct != null ? `${num(lastRun.run.marginRoiPct, 2)}%` : '—'}</strong></div>
            <div className="lab-c"><span>Omni revenue</span><strong>{usd(lastRun.run.omniRevenueUsd)}</strong></div>
            <div className="lab-c"><span>Avg hold</span><strong>{dur(lastRun.run.avgHoldMs)}</strong></div>
            <div className="lab-c"><span>Max drawdown</span><strong>{usd(lastRun.run.maxDrawdownUsd)}</strong></div>
            {lastRun.run.leverage && (
              <div className="lab-c"><span>Leverage (crypto / HIP-3)</span><strong>{lastRun.run.leverage.crypto}x / {lastRun.run.leverage.hip3}x</strong></div>
            )}
            {(lastRun.summary?.avgLeverage ?? lastRun.run.avgLeverage) != null && (
              <div className="lab-c lab-c-sub"><span>↳ avg used</span><strong>{num(lastRun.summary?.avgLeverage ?? lastRun.run.avgLeverage, 1)}x</strong></div>
            )}
            <div className="lab-c"><span>Margin/trade</span><strong>{usd(lastRun.run.marginUsd ?? lastRun.run.notionalUsd)}</strong></div>
            {lastRun.run.leverage && (
              <div className="lab-c"><span>Position (crypto)</span><strong>{usd((lastRun.run.marginUsd ?? 2000) * (lastRun.run.leverage.crypto ?? sizing.leverageCrypto ?? 5))}</strong></div>
            )}
          </div>
          {lastRun.exitReasons && (
            <div className="lab-exits">
              <span>Exit mix (count · avg net bps):</span>
              {Object.entries(lastRun.exitReasons)
                .filter(([, v]) => v > 0)
                .map(([k, v]) => (
                  <strong key={k}>
                    {k.replace(/_/g, ' ')} {v}
                    {lastRun.exitAvgNetBps?.[k] != null ? ` · ${num(lastRun.exitAvgNetBps[k], 2)} bps` : ''}
                  </strong>
                ))}
            </div>
          )}
          <table className="lab-table lab-compact">
            <thead>
              <tr>
                <th>Symbol</th><th>Trades</th><th>Wins</th><th>Lev</th>
                <th>Net (trader)</th><th>Omni rev.</th><th>Avg net bps</th><th>Avg hold</th>
                <th>Exits (TP / stale / stop / timeout)</th>
              </tr>
            </thead>
            <tbody>
              {(lastRun.perSymbol || []).map((s) => (
                <tr key={s.symbol}>
                  <td><strong>{s.symbol}</strong></td>
                  <td>{num(s.num_trades, 0)}</td>
                  <td>{num(s.num_wins, 0)}</td>
                  <td>{num(s.leverage, 1)}x</td>
                  <td className={s.net_usd >= 0 ? 'pos' : 'neg'}>{usd(s.net_usd)}</td>
                  <td>{usd(s.omni_revenue_usd)}</td>
                  <td>{num(s.avg_net_bps_per_trade, 2)}</td>
                  <td>{dur(s.avg_hold_ms)}</td>
                  <td className="lab-exitmix">
                    {s.exitReasons
                      ? `${s.exitReasons.take_profit || 0} / ${s.exitReasons.stale || 0} / ${s.exitReasons.stop || 0} / ${s.exitReasons.timeout || 0}`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h4>Run history</h4>
      {liveRun && (
        <div className="lab-live-banner">
          <strong>Live paper (forward)</strong> — {liveRun.label || 'live'} · {num(liveRun.numTrades, 0)} trades ·{' '}
          avg {num(liveRun.avgNetBpsPerTrade, 2)} bps · net {usd(liveRun.netUsd)}
          {liveRun.numTrades === 0 && (
            <span className="lab-note"> · waiting for SOL dislocations (entry ~14 bps)</span>
          )}
        </div>
      )}
      <div className="lab-tablewrap">
        <table className="lab-table lab-compact">
          <thead><tr><th>When</th><th>Kind</th><th>Label</th><th>Trades</th><th>Win</th><th>Net (trader)</th><th>Omni rev.</th><th>Avg bps</th></tr></thead>
          <tbody>
            {runs.filter((r) => r.kind !== 'live').map((r) => (
              <tr key={r.id} className={r.kind === 'live' ? 'lab-live' : ''}>
                <td>{r.createdAt ? new Date(r.createdAt + 'Z').toLocaleString() : '—'}</td>
                <td><span className={`lab-kind ${r.kind}`}>{r.kind}</span></td>
                <td>{r.label}</td>
                <td>{num(r.numTrades, 0)}</td>
                <td>{pct(r.winRate)}</td>
                <td className={r.netUsd >= 0 ? 'pos' : 'neg'}>{usd(r.netUsd)}</td>
                <td>{usd(r.omniRevenueUsd)}</td>
                <td>{num(r.avgNetBpsPerTrade, 2)}</td>
              </tr>
            ))}
            {!runs.length && <tr><td colSpan={8} className="lab-empty">No runs yet — run a backtest or enable live paper trading.</td></tr>}
          </tbody>
        </table>
      </div>
      </>
      )}
    </div>
  );
}
